import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ApplicationInstructions from "../../../Components/ApplicationInstructions";
import { getFieldData } from "../../../Services/appService";
import { getCurrentUser } from "../../store/auth";

import { useGetUserDetailsQuery } from "../../store/authApiSlice";
import { useLoadCandidateAppAllTasksQuery } from "../../store/candidateApiSlice";

const ONBOARDING_STYLES = {
    shell: {
        fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        width: "100%"
    },
    hero: {
        borderRadius: "20px",
        padding: "22px 26px",
        background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 52%, #f8fafc 100%)",
        border: "1px solid rgba(219, 234, 254, 0.95)",
        boxShadow: "0 10px 30px rgba(37, 99, 235, 0.05)",
        marginBottom: "16px"
    },
    heroRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: "14px",
        width: "100%"
    },
    topRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 0.8fr)",
        gap: "16px",
        alignItems: "stretch",
        width: "100%"
    },
    heroSummary: {
        width: "100%"
    },
    progressHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "12px"
    },
    progressLabel: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#0f172a"
    },
    progressMeta: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#2563eb"
    },
    progressMetaBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        borderRadius: "8px",
        background: "#eff6ff",
        border: "1px solid rgba(191, 219, 254, 0.9)"
    },
    progressTrack: {
        position: "relative",
        width: "100%",
        height: "12px",
        borderRadius: "999px",
        background: "linear-gradient(90deg, rgba(191, 219, 254, 0.38) 0%, rgba(224, 231, 255, 0.7) 100%)",
        overflow: "hidden",
        boxShadow: "inset 0 2px 4px rgba(15, 23, 42, 0.06)"
    },
    progressFill: {
        position: "relative",
        height: "100%",
        borderRadius: "999px",
        background: "linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)",
        boxShadow: "0 6px 18px rgba(37, 99, 235, 0.25)",
        transition: "width 0.2s ease"
    },
    progressMarkers: {
        position: "absolute",
        top: "50%",
        left: "12px",
        right: "12px",
        transform: "translateY(-50%)",
        display: "flex",
        justifyContent: "space-between",
        pointerEvents: "none"
    },
    progressMarker: {
        width: "2px",
        height: "16px",
        borderRadius: "999px",
        background: "rgba(255,255,255,0.65)"
    },
    progressStatsRow: {
        marginTop: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap"
    },
    progressCaption: {
        fontSize: "12px",
        lineHeight: 1.5,
        color: "#64748b"
    },
    progressFootnote: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#1d4ed8"
    },
    statsWrap: {
        display: "flex",
        alignItems: "stretch",
        width: "100%"
    },
    statPill: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "14px",
        padding: "14px 18px",
        borderRadius: "16px",
        background: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.95)",
        color: "#2563eb",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.03)",
        width: "100%",
        minWidth: 0,
        minHeight: "84px"
    },
    statPillApproved: {
        border: "1px solid rgba(167, 243, 208, 0.9)",
        background: "#ecfdf5",
        boxShadow: "0 6px 18px rgba(4, 120, 87, 0.04)"
    },
    statPillPending: {
        border: "1px solid rgba(226, 232, 240, 0.95)"
    },
    statIconWrap: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#eff6ff",
        border: "1px solid rgba(191, 219, 254, 0.9)",
        fontSize: "18px",
        flexShrink: 0
    },
    statIconWrapApproved: {
        color: "#047857",
        background: "#ffffff",
        border: "1px solid rgba(167, 243, 208, 0.9)"
    },
    statIconWrapPending: {
        color: "#2563eb"
    },
    statTextBlock: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    statSupport: {
        fontSize: "11px",
        fontWeight: 600,
        color: "#64748b",
        letterSpacing: "0.08em",
        textTransform: "uppercase"
    },
    statValue: {
        fontSize: "22px",
        fontWeight: 600,
        color: "#0f172a",
        lineHeight: 1.1
    },
    statLabel: {
        fontSize: "12px",
        lineHeight: 1.45,
        color: "#475569",
        marginTop: "4px"
    },
    priorityCallout: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "14px 16px",
        borderRadius: "16px",
        border: "1px solid rgba(253, 230, 138, 0.9)",
        background: "#fffbeb",
        boxShadow: "0 6px 18px rgba(180, 83, 9, 0.04)"
    },
    priorityIcon: {
        width: "36px",
        height: "36px",
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#ffffff",
        color: "#b45309",
        border: "1px solid rgba(253, 230, 138, 0.9)",
        fontSize: "16px",
        flexShrink: 0
    },
    priorityCopy: {
        minWidth: 0,
        flex: 1
    },
    priorityTitle: {
        fontSize: "14px",
        fontWeight: 600,
        color: "#0f172a",
        marginBottom: "2px"
    },
    priorityText: {
        fontSize: "12px",
        lineHeight: 1.5,
        color: "#475569",
        margin: 0,
        fontWeight: 600
    },
    actionRow: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap"
    },
    helpButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "8px",
        border: "1px solid rgba(203, 213, 225, 0.9)",
        background: "#ffffff",
        color: "#0f172a",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
        transition: "all 0.2s ease"
    },
    tabsWrap: {
        borderRadius: "16px",
        background: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.95)",
        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.04)",
        overflow: "visible",
        padding: "4px"
    },
    tabsRail: {
        position: "relative",
        padding: "6px 58px",
        overflow: "visible"
    },
    tabsScroller: {
        overflowX: "auto",
        overflowY: "visible",
        width: "100%",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        display: "flex",
        alignItems: "center"
    },
    tabsList: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        minWidth: "100%",
        width: "max-content",
        whiteSpace: "nowrap"
    },
    tabItemWrap: {
        position: "relative",
        display: "inline-flex",
        alignItems: "stretch",
        overflow: "visible"
    },
    tabTitleWrap: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        whiteSpace: "nowrap",
        flexWrap: "nowrap",
        position: "relative"
    },
    tabButton: {
        outline: "none",
        border: "1px solid transparent",
        borderRadius: "10px",
        background: "transparent",
        color: "#64748b",
        padding: "10px 16px",
        minHeight: "46px",
        fontSize: "13.5px",
        fontWeight: 600,
        lineHeight: 1.2,
        boxShadow: "none",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "nowrap",
        position: "relative",
        marginTop: "0",
        marginBottom: "0",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        flex: "0 0 auto",
        overflow: "visible",
        cursor: "pointer"
    },
    activeTabButton: {
        color: "#1d4ed8",
        fontWeight: 600,
        background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
        border: "1px solid rgba(191, 219, 254, 0.95)",
        borderBottom: "3px solid #2563eb",
        boxShadow: "0 4px 14px rgba(37, 99, 235, 0.12)",
        zIndex: 2
    },
    tabLabel: {
        fontSize: "13.5px",
        fontWeight: 600
    },
    tabIcon: {
        color: "#64748b",
        width: "18px",
        textAlign: "center",
        fontSize: "14px"
    },
    activeTabIcon: {
        color: "#2563eb"
    },
    stepStateIcon: {
        marginLeft: "4px",
        fontSize: "15px",
        opacity: 0.95
    },
    tabTooltip: {
        position: "fixed",
        transform: "translate(-50%, calc(-100% - 12px))",
        minWidth: "220px",
        maxWidth: "280px",
        padding: "12px 14px",
        borderRadius: "12px",
        background: "#ffffff",
        border: "1px solid rgba(226, 232, 240, 0.95)",
        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
        color: "#0f172a",
        textAlign: "left",
        whiteSpace: "normal",
        zIndex: 5,
        pointerEvents: "none",
        opacity: 0,
        visibility: "hidden",
        transition: "opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease"
    },
    tabTooltipVisible: {
        opacity: 1,
        visibility: "visible",
        transform: "translate(-50%, calc(-100% - 16px))"
    },
    tabTooltipArrow: {
        position: "absolute",
        left: "50%",
        bottom: "-7px",
        width: "14px",
        height: "14px",
        background: "#ffffff",
        borderRight: "1px solid rgba(226, 232, 240, 0.95)",
        borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
        transform: "translateX(-50%) rotate(45deg)"
    },
    tabTooltipEyebrow: {
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#2563eb",
        marginBottom: "4px"
    },
    tabTooltipTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        fontSize: "13px",
        fontWeight: 600,
        color: "#0f172a",
        marginBottom: "6px"
    },
    tabTooltipStatus: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "3px 8px",
        borderRadius: "8px",
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        flexShrink: 0
    },
    tabTooltipStatusDone: {
        background: "#ecfdf5",
        color: "#047857",
        border: "1px solid rgba(167, 243, 208, 0.9)"
    },
    tabTooltipStatusPending: {
        background: "#fffbeb",
        color: "#b45309",
        border: "1px solid rgba(253, 230, 138, 0.9)"
    },
    tabTooltipText: {
        fontSize: "12px",
        lineHeight: 1.45,
        color: "#64748b",
        margin: 0,
        fontWeight: 600
    },
    scrollButton: {
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: "34px",
        height: "34px",
        borderRadius: "8px",
        border: "1px solid rgba(203, 213, 225, 0.9)",
        background: "#ffffff",
        color: "#475569",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        zIndex: 3
    },
    scrollButtonLeft: {
        left: "12px"
    },
    scrollButtonRight: {
        right: "12px"
    }
};

const EMPTY_ARRAY = [];

export default function OnboardingSteps() {
    const [instructionShowHide, setInstructionShowHide] = useState(false);
    const [entityType, setEntityType] = useState("");
    const [appStatus, setAppStatus] = useState(false);
    const [appApproved, setAppApproved] = useState(false);
    const [appAllTasks, setAllAppTasks] = useState([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [tooltipState, setTooltipState] = useState(null);

    const tabsNavRef = useRef(null);

    const location = useLocation();
    const appType = location?.pathname;

    const navigate = useNavigate();
    const currentUser = getCurrentUser();

    let usrId = currentUser?.id;
    if (currentUser?.linkUser) usrId = currentUser.linkUser;

    const { data: userDetailsSelector } = useGetUserDetailsQuery(usrId, { skip: !usrId });
    const { data: candidateAppAllTasksSelector = EMPTY_ARRAY } = useLoadCandidateAppAllTasksQuery(usrId, { skip: !usrId });

    useEffect(() => {
        if (userDetailsSelector) {
            const entType = getFieldData(
                userDetailsSelector.performerData,
                "candidateonboardingform",
                "CandidateEntity"
            );
            setEntityType(entType);
            setAppStatus(userDetailsSelector?.appSubmit);
            setAppApproved(userDetailsSelector?.approved > 0);
        }
    }, [userDetailsSelector]);

    useEffect(() => {
        setAllAppTasks(candidateAppAllTasksSelector);
    }, [candidateAppAllTasksSelector]);

    const handleInstructionShowHide = () => {
        setInstructionShowHide(!instructionShowHide);
    };

    const handleSelect = (key) => {
        navigate(key);
    };

    const updateScrollState = useCallback(() => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const { scrollLeft, scrollWidth, clientWidth } = tabsNav;
        setCanScrollLeft(scrollLeft > 4);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    }, []);

    const handleTabsScroll = (direction) => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const amount = Math.max(220, Math.floor(tabsNav.clientWidth * 0.55));
        tabsNav.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth"
        });
    };

    const getStepIcon = (isDone) => (
        isDone
            ? <i className="fa fa-check-circle" style={{ color: "#16a34a", marginLeft: "4px" }} aria-hidden="true"></i>
            : <i className="fa fa-clock-o" style={{ color: "#d97706", marginLeft: "4px" }} aria-hidden="true"></i>
    );

    const getStepMenuIconClass = (stepKey) => {
        switch (stepKey) {
            case "/Onboarding":
            case "/DynamicForm":
                return "fa-file-text-o";
            case "/ContractDocs":
                return "fa-folder-open-o";
            case "/AchInfo":
                return "fa-university";
            case "/BackgroundChecks":
                return "fa-search";
            case "/OnboardingStatus":
                return "fa-clipboard";
            case "/ReportingDetails":
                return "fa-map-marker";
            case "/TimeLogs":
                return "fa-clock-o";
            case "/Compliance":
                return "fa-shield";
            case "/CustomReport":
                return "fa-bar-chart";
            default:
                return "fa-circle-o";
        }
    };

    const getStepStatusTooltip = (label, isDone, isActive) => {
        const statusText = isDone ? "Completed" : "Pending";
        const detailText = isActive
            ? `${label} is currently open in this workspace.`
            : `Open ${label} to review or complete this step.`;

        return {
            statusText,
            detailText
        };
    };

    const showTabTooltip = (event, tab, isActive) => {
        const tooltipContent = getStepStatusTooltip(tab.label, tab.done, isActive);
        const rect = event.currentTarget.getBoundingClientRect();

        setTooltipState({
            key: tab.key,
            left: rect.left + (rect.width / 2),
            top: rect.top,
            label: tab.label,
            done: tab.done,
            statusText: tooltipContent.statusText,
            detailText: tooltipContent.detailText
        });
    };

    const hideTabTooltip = (tabKey) => {
        setTooltipState((currentTooltip) => (
            currentTooltip && currentTooltip.key === tabKey ? null : currentTooltip
        ));
    };

    const renderTabTitle = (eventKey, label, isDone, isActive) => (
        <span style={ONBOARDING_STYLES.tabTitleWrap}>
            <i
                className={`fa ${getStepMenuIconClass(eventKey)}`}
                aria-hidden="true"
                style={{
                    ...ONBOARDING_STYLES.tabIcon,
                    ...(isActive ? ONBOARDING_STYLES.activeTabIcon : {})
                }}
            ></i>
            <span style={ONBOARDING_STYLES.tabLabel}>{label}</span>
            <span style={ONBOARDING_STYLES.stepStateIcon}>{getStepIcon(isDone)}</span>
        </span>
    );

    const allPaths = ["/Onboarding", "/ContractDocs", "/AchInfo", "/BackgroundChecks", "/ReportingDetails",
        "/OnboardingStatus", "/DynamicForm", "/TimeLogs", "/CustomReport", "/Compliance"];

    useEffect(() => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const rafId = window.requestAnimationFrame(() => {
            updateScrollState();
        });

        const handleResize = () => updateScrollState();
        const handleScroll = () => updateScrollState();
        let resizeObserver = null;

        tabsNav.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleResize);

        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => updateScrollState());
            resizeObserver.observe(tabsNav);
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            tabsNav.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [appType, appApproved, appStatus, entityType, appAllTasks, updateScrollState]);

    useEffect(() => {
        const tabsNav = tabsNavRef.current;
        if (!tabsNav) return;

        const activeTab = tabsNav.querySelector('[data-active="true"]');
        if (activeTab && typeof activeTab.scrollIntoView === "function") {
            activeTab.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
    }, [appType]);

    if (allPaths.indexOf(appType) > -1) {
        const activePath = appType === "/DynamicForm" ? "/Onboarding" : appType;
        const timeLogDone = appAllTasks.filter((x) => x.loanPhase_Desc === "Time Log").length > 0;
        const complianceDone = appAllTasks.filter((x) => x.loanPhase_Desc === "Compliance").length > 0;

        const tabItems = [
            { key: "/Onboarding", label: "Onboarding Form", done: appStatus },
            { key: "/ContractDocs", label: "Contract Documents", done: appApproved },
            ...(entityType !== "C2C Basis" ? [{ key: "/AchInfo", label: "ACH", done: appApproved }] : []),
            { key: "/BackgroundChecks", label: "Background Check", done: appApproved },
            { key: "/OnboardingStatus", label: "Onboarding Status", done: appApproved },
            { key: "/ReportingDetails", label: "Reporting Details", done: appApproved },
            { key: "/TimeLogs", label: entityType !== "C2C Basis" ? "Time Log and Payments" : "Time Log", done: timeLogDone },
            { key: "/Compliance", label: "Compliance", done: complianceDone },
            ...(entityType !== "C2C Basis" ? [{ key: "/CustomReport", label: "Reports", done: timeLogDone }] : [])
        ];

        const visibleStepCount = tabItems.length;
        const completedStepCount = tabItems.filter((tab) => tab.done).length;
        const incompleteStepCount = Math.max(visibleStepCount - completedStepCount, 0);
        const overallProgress = visibleStepCount > 0
            ? Math.round((completedStepCount / visibleStepCount) * 100)
            : 0;
        const onboardingFormStep = tabItems.find((tab) => tab.key === "/Onboarding");
        const isOnboardingFormComplete = onboardingFormStep ? onboardingFormStep.done : false;

        return <React.Fragment>
            <style>{`
                [data-onboarding-tabs-scroller="true"]::-webkit-scrollbar {
                    display: none;
                }
                .onboarding-step-tab-btn {
                    outline: none !important;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
                }
                .onboarding-step-tab-btn:hover:not([data-active="true"]) {
                    background-color: #eff6ff !important;
                    color: #1d4ed8 !important;
                    border-color: rgba(191, 219, 254, 0.7) !important;
                    transform: translateY(-1px);
                }
                .onboarding-step-tab-btn[data-active="true"] {
                    background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%) !important;
                    color: #1d4ed8 !important;
                    border: 1px solid rgba(191, 219, 254, 0.95) !important;
                    border-bottom: 3px solid #2563eb !important;
                    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.12) !important;
                    font-weight: 800 !important;
                }
                .onboarding-step-tab-btn:focus,
                .onboarding-step-tab-btn:focus-visible,
                .onboarding-step-tab-btn:active {
                    outline: none !important;
                    box-shadow: none !important;
                }
            `}</style>

            <div style={ONBOARDING_STYLES.hero}>
                <div style={ONBOARDING_STYLES.heroRow}>
                    <div style={ONBOARDING_STYLES.topRow}>
                        <div style={ONBOARDING_STYLES.heroSummary}>
                            <div style={ONBOARDING_STYLES.progressHeader}>
                                <span style={ONBOARDING_STYLES.progressLabel}>Overall progress</span>
                                <span style={{ ...ONBOARDING_STYLES.progressMeta, ...ONBOARDING_STYLES.progressMetaBadge }}>
                                    <i className="fa fa-flag-checkered" aria-hidden="true"></i>
                                    {overallProgress}% complete
                                </span>
                            </div>
                            <div style={ONBOARDING_STYLES.progressTrack}>
                                <div
                                    style={{
                                        ...ONBOARDING_STYLES.progressFill,
                                        width: `${overallProgress}%`
                                    }}
                                ></div>
                                <div style={ONBOARDING_STYLES.progressMarkers} aria-hidden="true">
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                    <span style={ONBOARDING_STYLES.progressMarker}></span>
                                </div>
                            </div>
                            <div style={ONBOARDING_STYLES.progressStatsRow}>
                                <div style={ONBOARDING_STYLES.progressCaption}>
                                    {completedStepCount} of {visibleStepCount} visible onboarding steps are complete.
                                </div>
                                <div style={ONBOARDING_STYLES.progressFootnote}>
                                    {incompleteStepCount === 0
                                        ? "Everything visible is finished"
                                        : `${incompleteStepCount} step${incompleteStepCount === 1 ? "" : "s"} remaining`}
                                </div>
                            </div>
                        </div>
                        <div style={ONBOARDING_STYLES.statsWrap}>
                            <span
                                style={{
                                    ...ONBOARDING_STYLES.statPill,
                                    ...(appApproved ? ONBOARDING_STYLES.statPillApproved : ONBOARDING_STYLES.statPillPending)
                                }}
                            >
                                <span
                                    style={{
                                        ...ONBOARDING_STYLES.statIconWrap,
                                        ...(appApproved ? ONBOARDING_STYLES.statIconWrapApproved : ONBOARDING_STYLES.statIconWrapPending)
                                    }}
                                >
                                    <i className={`fa ${appApproved ? "fa-check-circle" : "fa-clock-o"}`} aria-hidden="true"></i>
                                </span>
                                <span style={ONBOARDING_STYLES.statTextBlock}>
                                    <span style={ONBOARDING_STYLES.statSupport}>Onboarding Status</span>
                                    <span style={ONBOARDING_STYLES.statValue}>{appApproved ? "Approved" : "In Progress"}</span>
                                    <span style={ONBOARDING_STYLES.statLabel}>
                                        {appApproved
                                            ? "Your onboarding has cleared review and is ready for the next stage."
                                            : "Complete the remaining onboarding items to move into approval."}
                                    </span>
                                </span>
                            </span>
                        </div>
                    </div>

                    {!isOnboardingFormComplete && (
                        <div style={ONBOARDING_STYLES.priorityCallout}>
                            <span style={ONBOARDING_STYLES.priorityIcon}>
                                <i className="fa fa-bolt" aria-hidden="true"></i>
                            </span>
                            <div style={ONBOARDING_STYLES.priorityCopy}>
                                <div style={ONBOARDING_STYLES.priorityTitle}>Onboarding Form is the top priority</div>
                                <p style={ONBOARDING_STYLES.priorityText}>
                                    Please complete the Onboarding Form as soon as possible. It is the most important step and unlocks the rest of the onboarding workflow.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={ONBOARDING_STYLES.shell}>
                <div style={ONBOARDING_STYLES.actionRow}>
                    <button
                        type="button"
                        style={ONBOARDING_STYLES.helpButton}
                        onClick={handleInstructionShowHide}
                    >
                        <i className="fa fa-hand-o-right text-dark mr-2"></i>
                        Help for completing onboarding process
                    </button>
                </div>

                <div style={ONBOARDING_STYLES.tabsWrap}>
                    <div style={ONBOARDING_STYLES.tabsRail}>
                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                ...ONBOARDING_STYLES.scrollButton,
                                ...ONBOARDING_STYLES.scrollButtonLeft,
                                opacity: canScrollLeft ? 1 : 0.35,
                                pointerEvents: canScrollLeft ? "auto" : "none"
                            }}
                            onClick={() => handleTabsScroll("left")}
                            aria-label="Scroll tabs left"
                        >
                            <i className="fa fa-angle-left" aria-hidden="true"></i>
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                ...ONBOARDING_STYLES.scrollButton,
                                ...ONBOARDING_STYLES.scrollButtonRight,
                                opacity: canScrollRight ? 1 : 0.35,
                                pointerEvents: canScrollRight ? "auto" : "none"
                            }}
                            onClick={() => handleTabsScroll("right")}
                            aria-label="Scroll tabs right"
                        >
                            <i className="fa fa-angle-right" aria-hidden="true"></i>
                        </button>

                        <div ref={tabsNavRef} style={ONBOARDING_STYLES.tabsScroller} data-onboarding-tabs-scroller="true">
                            <div id="onboarding-step-tabs" style={ONBOARDING_STYLES.tabsList}>
                                {tabItems.map((tab) => {
                                    const isActive = tab.key === activePath;
                                    return (
                                        <div
                                            key={tab.key}
                                            onMouseEnter={(event) => showTabTooltip(event, tab, isActive)}
                                            onMouseLeave={() => hideTabTooltip(tab.key)}
                                            onFocus={(event) => showTabTooltip(event, tab, isActive)}
                                            onBlur={() => hideTabTooltip(tab.key)}
                                            style={ONBOARDING_STYLES.tabItemWrap}
                                        >
                                            <button
                                                type="button"
                                                className="onboarding-step-tab-btn"
                                                onClick={() => handleSelect(tab.key)}
                                                data-active={isActive ? "true" : "false"}
                                                style={{
                                                    ...ONBOARDING_STYLES.tabButton,
                                                    ...(isActive ? ONBOARDING_STYLES.activeTabButton : {})
                                                }}
                                            >
                                                {renderTabTitle(tab.key, tab.label, tab.done, isActive)}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {instructionShowHide && (
                    <ApplicationInstructions
                        instructionShowHide={instructionShowHide}
                        handleInstructionShowHide={handleInstructionShowHide}
                        entityType={entityType}
                    ></ApplicationInstructions>
                )}

                {tooltipState && (
                    <span
                        role="tooltip"
                        style={{
                            ...ONBOARDING_STYLES.tabTooltip,
                            ...ONBOARDING_STYLES.tabTooltipVisible,
                            left: `${tooltipState.left}px`,
                            top: `${tooltipState.top}px`
                        }}
                    >
                        <span style={ONBOARDING_STYLES.tabTooltipEyebrow}>Onboarding Step</span>
                        <span style={ONBOARDING_STYLES.tabTooltipTitle}>
                            <span>{tooltipState.label}</span>
                            <span
                                style={{
                                    ...ONBOARDING_STYLES.tabTooltipStatus,
                                    ...(tooltipState.done ? ONBOARDING_STYLES.tabTooltipStatusDone : ONBOARDING_STYLES.tabTooltipStatusPending)
                                }}
                            >
                                <i className={`fa ${tooltipState.done ? "fa-check-circle" : "fa-clock-o"}`} aria-hidden="true"></i>
                                {tooltipState.statusText}
                            </span>
                        </span>
                        <span style={ONBOARDING_STYLES.tabTooltipText}>{tooltipState.detailText}</span>
                        <span style={ONBOARDING_STYLES.tabTooltipArrow}></span>
                    </span>
                )}
            </div>
        </React.Fragment>;
    }

    return "";
}
